import { useState } from "react";
import BaseModal from "../../BaseComponents/BaseModal/BaseModal";
import SelectBox from "../../BaseComponents/SelectBox/SelectBox";
import styles from "./GenerateReceiptModal.module.css";

const GenerateReceiptModal = ({ open, onClose, payment, onGenerate }) => {
  const [recipientEmail, setRecipientEmail] = useState(payment?.customerEmail || "");
  const [recipientName, setRecipientName] = useState(payment?.customerName || "");
  const [template, setTemplate] = useState("standard");
  const [includeBreakdown, setIncludeBreakdown] = useState(true);
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);

  if (!payment) return null;

  const handleGenerate = async () => {
    setIsGenerating(true);
    
    const receiptData = {
      paymentId: payment.id,
      recipientEmail,
      recipientName,
      template,
      includeBreakdown,
      additionalNotes,
      sendEmail,
      payment: {
        reference: payment.reference,
        bookingRef: payment.bookingRef,
        amount: payment.amount,
        currency: payment.currency,
        date: payment.date,
        paymentMethod: payment.paymentMethod,
        account: payment.account,
      }
    };

    try {
      await onGenerate(receiptData);
      onClose();
    } catch (error) {
      console.error("Failed to generate receipt:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const footer = (
    <div className={styles.footerActions}>
      <button onClick={onClose} className={styles.cancelBtn} disabled={isGenerating}>
        Cancel
      </button>
      <button onClick={handleGenerate} className={styles.generateBtn} disabled={isGenerating}>
        {isGenerating ? "Generating..." : "Generate Receipt"}
      </button>
    </div>
  );

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title="Generate Payment Receipt"
      footer={footer}
      width="650px"
    >
      <div className={styles.content}>
        {/* Payment Summary */}
        <div className={styles.paymentSummary}>
          <div className={styles.summaryHeader}>
            <span className={styles.summaryIcon}>💳</span>
            <div className={styles.summaryInfo}>
              <div className={styles.summaryTitle}>Payment Details</div>
              <div className={styles.summaryMeta}>
                {payment.bookingRef} • {payment.date}
              </div>
            </div>
            <div className={styles.summaryAmount}>
              {payment.currency} {payment.amount.toLocaleString('en-US', { 
                minimumFractionDigits: 2, 
                maximumFractionDigits: 2 
              })}
            </div>
          </div>
        </div>

        {/* Recipient Information */}
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>Recipient Information</h4>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Recipient Name <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="Enter recipient name"
                className={styles.input}
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Email Address <span className={styles.required}>*</span>
              </label>
              <input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="recipient@email.com"
                className={styles.input}
                required
              />
            </div>
          </div>
        </div>

        {/* Receipt Options */}
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>Receipt Options</h4>
          
          <div className={styles.formGroup}>
            <label className={styles.label}>Receipt Template</label>
            <SelectBox
              value={template}
              options={[
                { label: "Standard Receipt", value: "standard" },
                { label: "Detailed Receipt", value: "detailed" },
                { label: "Minimal Receipt", value: "minimal" },
                { label: "Invoice Style", value: "invoice" },
              ]}
              onChange={(e) => setTemplate(e.target.value)}
            />
            <span className={styles.hint}>Choose the format for your receipt</span>
          </div>

          <div className={styles.checkboxGroup}>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={includeBreakdown}
                onChange={(e) => setIncludeBreakdown(e.target.checked)}
              />
              <span className={styles.checkboxLabel}>Include payment breakdown</span>
            </label>
            <span className={styles.checkboxHint}>
              Shows itemized details of the payment
            </span>
          </div>

          <div className={styles.checkboxGroup}>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
              />
              <span className={styles.checkboxLabel}>Send receipt via email</span>
            </label>
            <span className={styles.checkboxHint}>
              Automatically email the receipt to the recipient
            </span>
          </div>
        </div>

        {/* Additional Notes */}
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>Additional Notes (Optional)</h4>
          <textarea
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e.target.value)}
            placeholder="Add any additional information to include in the receipt..."
            className={styles.textarea}
            rows={4}
          />
          <span className={styles.hint}>
            This message will appear at the bottom of the receipt
          </span>
        </div>

        {/* Preview Info */}
        <div className={styles.previewInfo}>
          <span className={styles.previewIcon}>ℹ️</span>
          <div className={styles.previewText}>
            <strong>What happens next:</strong>
            <ul>
              <li>Receipt will be generated as a PDF document</li>
              {sendEmail && <li>Email will be sent to {recipientEmail}</li>}
              <li>A copy will be saved to your records</li>
              <li>Receipt number will be auto-generated</li>
            </ul>
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export default GenerateReceiptModal;